import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  TextField, 
  Button, 
  Typography, 
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Stack
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Indicateur de force du mot de passe
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: '', color: 'inherit' };
    
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;

    if (strength <= 2) return { strength, label: 'Faible', color: 'error.main' };
    if (strength <= 3) return { strength, label: 'Moyen', color: 'warning.main' };
    return { strength, label: 'Fort', color: 'success.main' };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError('Les mots de passe ne correspondent pas.');
    }

    if (password.length < 8) {
      return setError('Le mot de passe doit contenir au moins 8 caractères.');
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(true);
        // Connexion automatique avec le token reçu
        if (data.token && data.user) {
          localStorage.setItem('portalToken', data.token);
          authLogin(data.user);
          // Redirection selon le rôle
          const isAdmin = ['ADMIN', 'SUPERADMIN'].includes(data.user.role);
          setTimeout(() => navigate(isAdmin ? '/admin' : '/portal'), 1500);
        } else {
          setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        setError(data.error || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setError('Impossible de contacter le serveur');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <Container maxWidth="xs">
        <Box sx={{ mt: 10 }}>
          <Alert severity="error">Lien de réinitialisation invalide ou manquant.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ p: 4, width: '100%', backgroundColor: 'background.paper', backgroundImage: 'none', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2, color: '#6366f1' }}>
              <Lock size={28} />
            </Box>
          </Box>
          <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Nouveau mot de passe
          </Typography>
          <Typography variant="body2" align="center" sx={{ mb: 4, color: 'text.secondary' }}>
            Définissez votre nouveau mot de passe pour accéder à votre compte.
          </Typography>
          
          {success ? (
            <Alert severity="success">
              Mot de passe réinitialisé avec succès ! Connexion en cours...
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Nouveau mot de passe"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              
              {password && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
                  <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                    <Box 
                      sx={{ 
                        width: `${(passwordStrength.strength / 5) * 100}%`, 
                        height: '100%', 
                        bgcolor: passwordStrength.color,
                        transition: 'width 0.3s, background-color 0.3s'
                      }} 
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: passwordStrength.color, minWidth: 50 }}>
                    {passwordStrength.label}
                  </Typography>
                </Stack>
              )}

              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type={showConfirm ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={confirmPassword && password !== confirmPassword}
                helperText={confirmPassword && password !== confirmPassword ? 'Les mots de passe ne correspondent pas' : ''}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowConfirm(!showConfirm)}
                        edge="end"
                      >
                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                fullWidth
                variant="contained"
                type="submit"
                size="large"
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser mon mot de passe'}
              </Button>
            </form>
          )}
        </Card>
      </Box>
    </Container>
  );
}

export default ResetPassword;
